import React, { useMemo, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend
} from 'recharts';
import { chartTheme, getSeriesColor } from './chartTheme';
import { defaultValueFormatter } from './formatters';
import { YChartTooltip } from './YChartTooltip';
import { toggleLegendKey } from './legendUtils';

const HORIZONTAL_BAR_RADIUS = [0, 2, 2, 0];
const VERTICAL_BAR_RADIUS = [2, 2, 0, 0];

export function YBarChart({
  data = [],
  height = 240,
  layout = 'horizontal',
  categoryKey = 'name',
  bars = [],
  legend = true,
  enableLegendToggle = true,
  onLegendToggle,
  barGap = 2,
  barSize = 10,
  categoryAxisWidth,
  margin,
  tooltipLabelFormatter,
  tooltipValueFormatter = defaultValueFormatter,
  dataTestId
}) {
  const isHorizontalBars = layout === 'vertical';

  const initialActive = useMemo(() => {
    if (!Array.isArray(bars) || bars.length === 0) return [];
    return bars.map((item) => item.dataKey || item.name).filter(Boolean);
  }, [bars]);

  const [activeKeys, setActiveKeys] = useState(initialActive);

  useEffect(() => {
    setActiveKeys(initialActive);
  }, [initialActive]);

  const resolvedBars = useMemo(() => {
    return bars.map((bar, index) => {
      const fill = getSeriesColor(index, bar.color || bar.fill);
      const radius = bar.radius ?? (isHorizontalBars ? HORIZONTAL_BAR_RADIUS : VERTICAL_BAR_RADIUS);
      return {
        ...bar,
        fill,
        radius,
        dataKey: bar.dataKey || bar.name
      };
    });
  }, [bars, isHorizontalBars]);

  const resolvedMargin = useMemo(() => {
    if (margin) return margin;
    if (isHorizontalBars) {
      return { top: 8, right: 16, left: 4, bottom: 8 };
    }
    return { top: 10, right: 20, left: 10, bottom: 10 };
  }, [margin, isHorizontalBars]);

  const yAxisWidth = categoryAxisWidth ?? (isHorizontalBars ? 88 : undefined);

  const handleLegendClick = useCallback(
    (entry) => {
      const key = entry?.dataKey || entry?.value;
      setActiveKeys((prev) => {
        const next = toggleLegendKey(prev, key);
        if (next !== prev && onLegendToggle) {
          onLegendToggle(next);
        }
        return next;
      });
    },
    [onLegendToggle]
  );

  const legendFormatter = useCallback(
    (value, entry) => {
      const key = entry?.dataKey || value;
      const isActive = !enableLegendToggle || activeKeys.includes(key);
      return <span style={{ opacity: isActive ? 1 : 0.5 }}>{value}</span>;
    },
    [activeKeys, enableLegendToggle]
  );

  return (
    <div className="y-chart" style={{ height }} data-testid={dataTestId}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={data}
          layout={layout}
          margin={resolvedMargin}
          barGap={barGap}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            stroke={chartTheme.gridColor}
            horizontal={!isHorizontalBars}
            vertical={isHorizontalBars}
          />
          {isHorizontalBars ? (
            <>
              <XAxis
                type="number"
                axisLine={false}
                tickLine={chartTheme.tickLine}
                stroke={chartTheme.axisColor}
                fontSize={chartTheme.axisFontSize}
                allowDecimals={false}
              />
              <YAxis
                type="category"
                dataKey={categoryKey}
                axisLine={false}
                tickLine={chartTheme.tickLine}
                stroke={chartTheme.axisColor}
                fontSize={chartTheme.axisFontSize}
                width={yAxisWidth}
              />
            </>
          ) : (
            <>
              <XAxis
                dataKey={categoryKey}
                axisLine={false}
                tickLine={chartTheme.tickLine}
                stroke={chartTheme.axisColor}
                fontSize={chartTheme.axisFontSize}
              />
              <YAxis
                type="number"
                axisLine={false}
                tickLine={chartTheme.tickLine}
                stroke={chartTheme.axisColor}
                fontSize={chartTheme.axisFontSize}
                allowDecimals={false}
              />
            </>
          )}
          <Tooltip
            cursor={false}
            content={
              <YChartTooltip
                labelFormatter={tooltipLabelFormatter}
                valueFormatter={tooltipValueFormatter}
              />
            }
          />
          {legend && (
            <Legend
              verticalAlign="top"
              wrapperStyle={{ paddingBottom: 8, fontSize: 11, fontFamily: 'var(--font-mono)' }}
              iconType="circle"
              onClick={enableLegendToggle ? handleLegendClick : undefined}
              formatter={legendFormatter}
            />
          )}
          {resolvedBars.map((bar) => {
            const key = bar.dataKey || bar.name;
            const isActive = !legend || activeKeys.includes(key);
            return (
              <Bar
                key={key}
                {...bar}
                hide={!isActive}
                barSize={bar.barSize ?? barSize}
              />
            );
          })}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

YBarChart.propTypes = {
  data: PropTypes.array,
  height: PropTypes.number,
  layout: PropTypes.oneOf(['horizontal', 'vertical']),
  categoryKey: PropTypes.string,
  bars: PropTypes.arrayOf(PropTypes.object),
  legend: PropTypes.bool,
  enableLegendToggle: PropTypes.bool,
  onLegendToggle: PropTypes.func,
  barGap: PropTypes.number,
  barSize: PropTypes.number,
  categoryAxisWidth: PropTypes.number,
  margin: PropTypes.object,
  tooltipLabelFormatter: PropTypes.func,
  tooltipValueFormatter: PropTypes.func,
  dataTestId: PropTypes.string
};

export default YBarChart;
